import React, { useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Visibility as ViewIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import {
  OrderWithClient,
  updateOrderStatusAdmin,
  downloadOrderFiles,
} from "../../utils/adminOrderUtils";

interface OrdersTabProps {
  orders: OrderWithClient[];
  onRefresh: () => void;
  onOrderUpdate: (updatedOrder: OrderWithClient) => void;
}

const ORDER_STATUSES = [
  { value: "pending", label: "Pending", color: "warning" as const },
  { value: "awaiting-info", label: "Awaiting Info", color: "info" as const },
  { value: "in-progress", label: "In Progress", color: "primary" as const },
  { value: "completed", label: "Completed", color: "success" as const },
  { value: "cancelled", label: "Cancelled", color: "error" as const },
];

const OrdersTab: React.FC<OrdersTabProps> = ({
  orders,
  onRefresh,
  onOrderUpdate,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedOrder, setSelectedOrder] = useState<OrderWithClient | null>(
    null
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const handleViewOrder = (order: OrderWithClient) => {
    setSelectedOrder(order);
  };

  const handleEditOrder = (order: OrderWithClient) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setAdminNotes(order.adminNotes || "");
    setEditDialogOpen(true);
  };

  const handleDownloadFiles = async (order: OrderWithClient) => {
    try {
      setLoading(true);
      await downloadOrderFiles(order.id);
      enqueueSnackbar("Files downloaded successfully", { variant: "success" });
    } catch (error) {
      console.error("Error downloading files:", error);
      enqueueSnackbar("Error downloading files", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrder = async () => {
    if (!selectedOrder) return;

    try {
      setLoading(true);
      const updatedOrder = await updateOrderStatusAdmin(
        selectedOrder.id,
        newStatus,
        {
          reason: "admin-update",
          notes: adminNotes,
          adminUserId: "admin", // You can get this from auth context
        }
      );

      onOrderUpdate({ ...selectedOrder, ...updatedOrder });
      setEditDialogOpen(false);
      enqueueSnackbar("Order updated successfully", { variant: "success" });
    } catch (error) {
      console.error("Error updating order:", error);
      enqueueSnackbar("Error updating order", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status: string) => {
    const statusConfig = ORDER_STATUSES.find((s) => s.value === status);
    return (
      <Chip
        label={statusConfig?.label || status}
        color={statusConfig?.color || "default"}
        size="small"
      />
    );
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "N/A";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h6">Orders Management</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={onRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {orders.length === 0 ? (
        <Alert severity="info">No orders found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Order ID</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {order.id.substring(0, 8)}...
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.clientName || order.clientEmail}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {order.clientEmail}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {order.productType || "N/A"}
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(order.status)}</TableCell>
                  <TableCell>
                    {order.totalAmount
                      ? formatCurrency(order.totalAmount)
                      : "N/A"}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(order.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewOrder(order)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Order">
                        <IconButton
                          size="small"
                          onClick={() => handleEditOrder(order)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Download Files">
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFiles(order)}
                          disabled={loading}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Edit Order Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Order</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              select
              label="Status"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              fullWidth
            >
              {ORDER_STATUSES.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Admin Notes"
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              multiline
              rows={3}
              fullWidth
              placeholder="Add notes about this status change..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleUpdateOrder}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={20} /> : "Update Order"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Order Dialog */}
      <Dialog
        open={!!selectedOrder && !editDialogOpen}
        onClose={() => setSelectedOrder(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box sx={{ pt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Order #{selectedOrder.id}
              </Typography>
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                <Box>
                  <Typography variant="subtitle2">
                    Client Information
                  </Typography>
                  <Typography>
                    Name: {selectedOrder.clientName || "N/A"}
                  </Typography>
                  <Typography>Email: {selectedOrder.clientEmail}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2">Order Information</Typography>
                  <Typography>
                    Status: {getStatusChip(selectedOrder.status)}
                  </Typography>
                  <Typography>
                    Amount:{" "}
                    {selectedOrder.totalAmount
                      ? formatCurrency(selectedOrder.totalAmount)
                      : "N/A"}
                  </Typography>
                  <Typography>
                    Created: {formatDate(selectedOrder.createdAt)}
                  </Typography>
                </Box>
              </Box>
              {selectedOrder.adminNotes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2">Admin Notes</Typography>
                  <Typography>{selectedOrder.adminNotes}</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedOrder(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrdersTab;
